// src/storage/useSiteContentBridge.js -- H5 ripple (WP-5 Slice 2).
//
// Loads admin-edited copy/config from Firestore (/siteContent + /config) and
// bridges it into the app's copy state, so student-facing pages reflect what the
// admin published. The public hook excludes auth-only Tutor Review settings;
// AuthenticatedSiteContentBridge below reads those after confirmed sign-in.
// Mock mode / no-db: inert -> the
// app keeps its DEFAULT_* configs (Sprint-3 invariant; existing copy never breaks).
// Missing Firestore docs also fall through to defaults.
import { useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { SITE_AREAS, CONFIG_AREAS } from '../admin/contentAreas.js';
import { useAuth } from '../auth/useAuth.js';
import { DEFAULT_TUTOR_REVIEW_SETTINGS } from '../config/tutorReview.js';

const TUTOR_REVIEW_KEY = 'tutor-review-settings';

export function useSiteContentBridge(state) {
    useEffect(() => {
        if (AUTH_MODE === 'mock' || !db) return; // inert in mock: defaults stand
        let cancelled = false;
        (async () => {
            try {
                const tasks = [];
                for (const [key, area] of Object.entries(SITE_AREAS)) {
                    tasks.push(getDoc(doc(db, 'siteContent', key)).then((snap) => {
                        if (!cancelled && snap.exists() && snap.data().content) area.write(state, snap.data().content);
                    }));
                }
                for (const [key, area] of Object.entries(CONFIG_AREAS)) {
                    // This document is intentionally auth-only in Firestore rules.
                    if (key === TUTOR_REVIEW_KEY) continue;
                    tasks.push(getDoc(doc(db, 'config', key)).then((snap) => {
                        if (!cancelled && snap.exists() && snap.data().content) area.write(state, snap.data().content);
                    }));
                }
                await Promise.all(tasks);
            } catch (e) {
                // Non-fatal: defaults remain. Do not break the app over copy fetch.
                console.error('useSiteContentBridge:', e);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // once on mount
}

// Mount below AuthProvider. Public copy never waits for authentication; this
// separate read follows confirmed sign-in/account changes without changing rules
// or AuthProvider's existing profile-sync barrier.
export function AuthenticatedSiteContentBridge({ state }) {
    const { currentUser, loading } = useAuth();
    const uid = currentUser?.uid || null;
    const setSettings = state.setTutorReviewSettings;
    useEffect(() => {
        if (AUTH_MODE === 'mock' || !db) return;
        let cancelled = false;
        // Do not retain another session's/cached settings after sign-out, account
        // change or a failed/missing read. Prices and payments remain server-side.
        setSettings({ ...DEFAULT_TUTOR_REVIEW_SETTINGS });
        if (!loading && uid) {
            (async () => {
                try {
                    const snap = await getDoc(doc(db, 'config', TUTOR_REVIEW_KEY));
                    if (!cancelled && snap.exists() && snap.data().content) {
                        setSettings(snap.data().content);
                    }
                } catch (e) {
                    if (!cancelled) console.error('AuthenticatedSiteContentBridge:', e);
                }
            })();
        }
        return () => { cancelled = true; };
    }, [uid, loading, setSettings]);
    return null;
}

export default useSiteContentBridge;
